// A small JSX → function-call transform for widget code, so widgets can be pasted the way
// Figma's docs write them (<AutoLayout onClick={…}><Text>{count}</Text></AutoLayout>).
// Handles elements, fragments, attributes (strings, {expressions}, {...spread}, bare flags),
// text and {expression} children, and JSX nested inside expressions. Not handled: regex
// literals that contain "<", and TypeScript generics (write plain JavaScript).

const PRAGMA = 'figma.widget.h';

export function transformJsx(src: string): string {
  let out = '';
  let i = 0;
  let prev = ''; // last significant character (to tell "<" as JSX from "less than")
  const n = src.length;
  while (i < n) {
    const c = src[i];
    // strings, template literals and comments pass through untouched
    if (c === '"' || c === "'") {
      const j = skipString(src, i);
      out += src.slice(i, j);
      i = j;
      prev = c;
      continue;
    }
    if (c === '`') {
      const j = skipTemplate(src, i);
      out += src.slice(i, j);
      i = j;
      prev = '`';
      continue;
    }
    if (c === '/' && src[i + 1] === '/') {
      const j = src.indexOf('\n', i);
      out += src.slice(i, j < 0 ? n : j);
      i = j < 0 ? n : j;
      continue;
    }
    if (c === '/' && src[i + 1] === '*') {
      const j = src.indexOf('*/', i + 2);
      out += src.slice(i, j < 0 ? n : j + 2);
      i = j < 0 ? n : j + 2;
      continue;
    }
    if (c === '<' && startsJsx(src, i, prev, out)) {
      const { code, end } = parseElement(src, i);
      out += code;
      i = end;
      prev = ')';
      continue;
    }
    out += c;
    if (!/\s/.test(c)) prev = c;
    i++;
  }
  return out;
}

function startsJsx(src: string, i: number, prev: string, out: string): boolean {
  const next = src[i + 1];
  if (!(next === '>' || /[A-Za-z_$]/.test(next ?? ''))) return false;
  if (prev === '' || '([{,;=:?!&|>'.includes(prev)) return true;
  // after "return" / "=>" / "yield"
  return /(?:\breturn|=>|\byield|\bdefault)\s*$/.test(out);
}

function skipString(src: string, i: number): number {
  const q = src[i];
  let j = i + 1;
  while (j < src.length && src[j] !== q) {
    if (src[j] === '\\') j++;
    j++;
  }
  return j + 1;
}

function skipTemplate(src: string, i: number): number {
  let j = i + 1;
  while (j < src.length && src[j] !== '`') {
    if (src[j] === '\\') j += 2;
    else if (src[j] === '$' && src[j + 1] === '{') j = matchBrace(src, j + 1) + 1;
    else j++;
  }
  return j + 1;
}

/** Index of the "}" matching the "{" at i (skipping strings, templates and nested JSX). */
function matchBrace(src: string, i: number): number {
  let depth = 0;
  for (let j = i; j < src.length; j++) {
    const c = src[j];
    if (c === '"' || c === "'") j = skipString(src, j) - 1;
    else if (c === '`') j = skipTemplate(src, j) - 1;
    else if (c === '{') depth++;
    else if (c === '}' && --depth === 0) return j;
  }
  throw new Error('JSX: unbalanced {');
}

function readName(src: string, i: number): { name: string; end: number } {
  const m = /^[A-Za-z_$][\w$.:-]*/.exec(src.slice(i));
  return { name: m ? m[0] : '', end: i + (m ? m[0].length : 0) };
}

function skipWs(src: string, i: number) {
  while (i < src.length && /\s/.test(src[i])) i++;
  return i;
}

function parseElement(src: string, i: number): { code: string; end: number } {
  // "<" already at i
  i++;
  let tag = 'Fragment';
  let fragment = true;
  if (src[i] !== '>') {
    const r = readName(src, i);
    tag = r.name;
    i = r.end;
    fragment = false;
  }
  const props: string[] = [];
  // attributes
  for (;;) {
    i = skipWs(src, i);
    if (src[i] === '/' && src[i + 1] === '>') {
      return { code: call(tag, fragment, props, []), end: i + 2 };
    }
    if (src[i] === '>') {
      i++;
      break;
    }
    if (src[i] === '{') {
      // {...spread}
      const j = matchBrace(src, i);
      const inner = src.slice(i + 1, j).trim();
      props.push(transformJsx(inner));
      i = j + 1;
      continue;
    }
    const r = readName(src, i);
    if (!r.name) throw new Error(`JSX: unexpected "${src[i]}" in <${tag}>`);
    i = skipWs(src, r.end);
    let value = 'true';
    if (src[i] === '=') {
      i = skipWs(src, i + 1);
      if (src[i] === '"' || src[i] === "'") {
        const j = skipString(src, i);
        value = JSON.stringify(src.slice(i + 1, j - 1));
        i = j;
      } else if (src[i] === '{') {
        const j = matchBrace(src, i);
        value = `(${transformJsx(src.slice(i + 1, j))})`;
        i = j + 1;
      } else if (src[i] === '<') {
        const el = parseElement(src, i);
        value = el.code;
        i = el.end;
      }
    }
    props.push(`${JSON.stringify(r.name)}: ${value}`);
  }
  // children
  const children: string[] = [];
  let text = '';
  const flushText = () => {
    const t = jsxText(text);
    if (t) children.push(JSON.stringify(t));
    text = '';
  };
  for (;;) {
    if (i >= src.length) throw new Error(`JSX: <${tag}> is not closed`);
    if (src[i] === '<' && src[i + 1] === '/') {
      flushText();
      const close = src.indexOf('>', i);
      return { code: call(tag, fragment, props, children), end: close + 1 };
    }
    if (src[i] === '<') {
      flushText();
      const el = parseElement(src, i);
      children.push(el.code);
      i = el.end;
      continue;
    }
    if (src[i] === '{') {
      flushText();
      const j = matchBrace(src, i);
      const inner = src.slice(i + 1, j).trim();
      if (inner && !/^\/\*[\s\S]*\*\/$/.test(inner)) children.push(`(${transformJsx(inner)})`);
      i = j + 1;
      continue;
    }
    text += src[i++];
  }
}

/** JSX whitespace: trim each line, drop empty lines, join with spaces. */
function jsxText(t: string): string {
  const lines = t.split(/\r?\n/);
  return lines
    .map((l, idx) => {
      let s = l.replace(/\t/g, ' ');
      if (idx > 0) s = s.replace(/^\s+/, '');
      if (idx < lines.length - 1) s = s.replace(/\s+$/, '');
      return s;
    })
    .filter((l) => l.length)
    .join(' ');
}

function call(tag: string, fragment: boolean, props: string[], children: string[]) {
  const type = fragment ? '"Fragment"' : /^[a-z]/.test(tag) ? JSON.stringify(tag) : tag;
  const p = props.length ? `{${props.map((x) => (x.startsWith('...') ? x : x)).join(', ')}}` : 'null';
  return `${PRAGMA}(${[type, p, ...children].join(', ')})`;
}
